<template>
  <div class="text-left">
    <v-row>
      <v-col cols="12" class="mb-2">
        <h3>{{ $t("title.publicationData") }}</h3>
      </v-col>
    </v-row>
    <div class="required-label">
      <h5 class="mb-2">{{ $t("form.title") }}</h5>
      <v-chip x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </div>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.title"
          :label="''"
          :placeholder="$t('form.title')"
          required
          outlined
          :readonly="disabledForm"
          :rules="rules.char_required_max_250"
          :counter="250"
        ></v-text-field>
      </v-col>
      <v-col cols="12" class="py-0" v-if="similarities && similarities.length > 0">
        <v-alert
          class="mb-8 d-block"
          style="max-width: 100%; overflow: hidden; text-overflow: ellipsis;"
          color="yellow darken-4"
          icon="mdi-alert-box"
          type="warning"
          text
        >
          {{  $t('form.publication.similarities') }}
          <v-list light style="margin-left:-0.5em;">
            <v-list-item v-for="similarity in similarities" :key="similarity.id">
              <v-list-item-content>
                <v-list-item-title dark>
                  <v-row>
                    <v-col 
                      cols="12"
                      md="8"
                    >
                      <a :href="'https://citius.gal/research/publications/' + similarity.slug" target="_blank" style="white-space: break-spaces;">{{ similarity.title }}</a><br>
                      <span style="white-space: break-spaces;">{{ similarity.authors_text }}</span><br>
                      <span>DOI: 
                        <a :href="'https://doi.org/' + similarity.doi" target="_blank" style="white-space: break-spaces;">{{ similarity.doi }}</a></span>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      class="d-flex"
                    >
                      <v-spacer />
                      <v-btn
                        color="primary"
                        :to = "'/publications/' + similarity.id"
                      >
                        <v-icon left>mdi-pencil</v-icon>
                        {{ $t("button.sendModifications") }}
                      </v-btn>
                    </v-col> 
                  </v-row>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6" class="py-0">
        <div class="required-label">
          <h5 class="mb-0">{{ $t('form.publication.language') }}</h5>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <v-select
          v-model="publication.language"
          :items="languageOptions"
          item-text="text"
          item-value="value"
          :label="''"
          :placeholder="$t('form.publication.language')"
          :rules="rules.fieldRequired"
          :readonly="disabledForm"
          outlined
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h5 class="py-0">
          {{ $t("form.publication.visibility") }}
        </h5>
        <v-row>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              :readonly="disabledForm"
              v-model="publication.is_external"
              :label="$t('form.publication.external')"
            />
          </v-col>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              :readonly="disabledForm"
              v-model="publication.hidden"
              :label="$t('form.hidden')"
            />
          </v-col>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              :readonly="disabledForm"
              v-model="publication.featured"
              :label="$t('form.featured')"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <div
      class="minerva-sync-panel"
      :style="{ borderLeftColor: minervaStatusBorderColor(publication.minerva_sync_status) }"
    >
      <h5 class="pt-4 pb-1">
        {{ $t('form.publication.minervaSyncStatus') }}
      </h5>
      <p class="caption text--secondary mb-3">
        {{ $t('form.publication.minervaSyncHelp') }}
      </p>
      <v-row>
        <v-col cols="12" md="6" class="py-0">
          <v-select
            v-model="publication.minerva_sync_option"
            :items="minervaSyncOptions"
            item-text="text"
            item-value="value"
            :label="$t('form.publication.minervaVisibility')"
            :hint="$t('form.publication.minervaVisibilityHelp')"
            persistent-hint
            :readonly="disabledForm"
            outlined
            clearable
          />
        </v-col>
      </v-row>
    </div>
    <h5 class="form-section-title">
      {{ $t("form.abstract") }}
    </h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-textarea
          v-model="publication.abstract"
          :label="$t('form.abstract')"
          outlined
          :readonly="disabledForm"
          :rules="rules.char_max_6500"
          :counter="6500"
        ></v-textarea>
      </v-col>
    </v-row>
    <h5 class="form-section-title">{{ $t("form.keywords") }}</h5>
    <ArrayFieldForm
      v-model="publication.keywords"
      :read-only="disabledForm"
      name-field="keywords"
      :label="$t('form.keywords')"
    />

    <v-row>
      <v-col cols="12" sm="8" class="py-0">
        <h5>{{ $t("form.authors_text") }}</h5>
        <v-row>
          <v-col class="py-0">
            <v-text-field
              v-model="publication.authors_text"
              :label="$t('form.authors_text')"
              required
              outlined
              :readonly="disabledForm"
              :rules="rules.char_max_400"
              :counter="400"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" sm="4" class="py-0">
        <div class="required-label">
          <h5 class="mb-2">{{ $t("form.num_authors") }}</h5>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <v-row>
          <v-col class="py-0">
            <v-text-field
              v-model="publication.num_authors"
              :label="''"
              :placeholder="$t('form.num_authors')"
              required
              :readonly="disabledForm"
              outlined
              :rules="rules.numAuthorsRules"
              type="number"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <h5>{{ $t("form.authorsrel") }}</h5>
    <SelectPeopleComboBox
      :disabled-form="disabledForm"
      :items-selected="publication.authorsrel"
      :text-authors="publication.authors_text"
    />
    <h5 class="form-section-title">
      {{ $t("form.acknowledgements") }}
    </h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-textarea
          v-model="publication.acknowledgements"
          :readonly="disabledForm"
          :label="$t('form.acknowledgements')"
          outlined
          :rules="rules.char_max_3500"
          :counter="3500"
        ></v-textarea>
      </v-col>
    </v-row>
    <h5 class="form-section-title">
      {{ $t('form.doi') }}
    </h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.doi"
          :label="$t('form.doi')"
          required
          :readonly="disabledForm"
          outlined
          :rules="rules.doiRules"
        ></v-text-field>
      </v-col>
    </v-row>
    <h5 class="form-section-title">
      {{ $t('form.isbn') }}
    </h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.isbn"
          :label="$t('form.isbn')"
          required
          :readonly="disabledForm"
          outlined
          :rules="rules.isbnRules"
        ></v-text-field>
      </v-col>
    </v-row>
    <h5 class="form-section-title">
      {{ $t("form.publication.research_areas") }}
    </h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-select
          :items="researchAreas"
          v-model="publication.research_areas"
          :label="$t('form.publication.research_areas')"
          required
          :item-text="'name.' + $i18n.locale"
          item-value="id"
          outlined
          :readonly="disabledForm"
          multiple
        ></v-select>
      </v-col>
    </v-row>
    <div class="required-label">
      <h5 class="mb-0">{{ $t("collaborations") }}</h5>
      <v-chip x-small outlined color="primary">
        {{ $t("form.mandatory") }}
      </v-chip>
    </div>
    <v-row>
      <v-col cols="12" sm="6" lg="4" class="py-0">
        <v-checkbox
          class="big"
          v-model="publication.regional"
          :label="$t('form.publication.regional')"
          :indeterminate="publication.regional === ''"
          :true-value="true"
          :false-value="false"
          :rules="rules.checkboxRequired"
        />
      </v-col>
      <v-col cols="12" sm="6" lg="4" class="py-0">
        <v-checkbox
          class="big"
          v-model="publication.national"
          :label="$t('form.publication.national')"
          :indeterminate="publication.national === ''"
          :true-value="true"
          :false-value="false"
          :rules="rules.checkboxRequired"
        />
      </v-col>
      <v-col cols="12" lg="4" class="py-0">
        <v-checkbox
          class="big"
          v-model="publication.international"
          :label="$t('form.publication.international')"
          :indeterminate="publication.international === ''"
          :true-value="true"
          :false-value="false"
          :rules="rules.checkboxRequired"
        />
      </v-col>
    </v-row>

    <h5 class="form-section-title">Links</h5>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.links.publisher"
          :label="$t('label.links.publisher')"
          outlined
          :readonly="disabledForm"
        />
      </v-col>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.links.institutional_repository"
          :label="$t('label.links.institutional_repository')"
          outlined
          :readonly="disabledForm"
        />
      </v-col>
      <v-col cols="12" class="py-0">
        <v-text-field
          v-model="publication.links.personal_repository"
          :label="$t('label.links.personal_repository')"
          outlined
          :readonly="disabledForm"
        />
      </v-col>
      <v-col cols="12" v-if="teseo_link" class="py-0">
        <v-text-field
          v-model="publication.links.teseo"
          :label="$t('label.links.teseo')"
          outlined
          :readonly="disabledForm"
        />
      </v-col>
    </v-row>

    <h5>{{ $t("form.files") }}</h5>
    <v-alert
      v-if="modOp_id === null"
      dense
      text
      type="info"
      class="without-margin-bottom"
    >
      {{ $t("form.publication.files.info") }}
    </v-alert>
    <v-alert
      dense
      text
      type="info"
      class="without-margin-bottom"
    >
      {{ $t("form.publication.files.size_warning") }}
    </v-alert>
    <v-card min-height="200px" elevation="0">
      <UploadFiles
        :read-only="disabledForm"
        object_type="publication"
        :id="this.modOp_id"
        :final="false"
        @change-files="changeFiles"
        @get-files="changeFiles"
      />
      <v-row>
        <v-overlay :opacity="0.85" :absolute="true" :value="modOp_id === null" color="#ffffff">
          <v-btn color="secondary" @click="$emit('save-form')">
            {{ $t("button.saveForm") }}
          </v-btn>
        </v-overlay>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import UploadFiles from "../../reusables/UploadFiles";
import SelectPeopleComboBox from "../../reusables/SelectPeopleComboBox";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import validationRules from "../../../mixins/validationRules";
import publication_model from "../../../mixins/model_utils/publication_model";
import minervaDisplay from "../../../mixins/minervaDisplay";
import axios from "axios";
export default {
  components: { UploadFiles, SelectPeopleComboBox, ArrayFieldForm },
  name: "NewPublication",
  mixins: [validationRules, publication_model, minervaDisplay],
  props: {
    similarities: {
      type: Array,
      default: () => [],
    },
    publication: Object,
    modOp_id: Number,
    disabledForm: Boolean,
    teseo_link: {
      type: Boolean,
      default: false
    }
  },

  created() {
    this.getResearchAreas();
    this.getLanguageOptions();
    this.getMinervaSyncOptions();
  },

  watch:{
    'publication.title':{
      handler: function(val, oldVal){
        if(val !== oldVal){
          axios({ url: this.$store.state.endpoint + "/similar/publication", method: "GET", params: { search: this.publication.title } })
          .then(resp => {
            this.similarities = resp.data;
          });
        }
      }
    }
  },

  methods: {
    changeFiles(files){
      this.$emit("change-files", files);
    }
  },

};
</script>

<style scoped>
.required-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.required-label .v-chip {
  margin-left: 8px;
}

.form-section-title {
  margin-bottom: 0.85rem;
}

.minerva-sync-panel {
  border-left: 4px solid var(--v-primary-base);
  padding: 0.1rem 0 0.35rem 1rem;
  margin-bottom: 0.5rem;
}
</style>
